import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';

import { OrderTypeEnum } from '@flipster/apis/types';
import { triggerPriceTypeEnum, type TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { authBunja, userIdBunja } from '@flipster/features/src/shared/auth/state';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { accountBunja } from '@flipster/r230522/original/account';
import { marginsBunja } from '@flipster/r230522/original/margin';
import { PendingPosition, ordersBunja } from '@flipster/r230522/original/order';
import { OpenPosition, openPositionsBunja, positionsBunja } from '@flipster/r230522/original/position';
import { getTickerDisplayPriceOrDefault } from '@flipster/r230522/original/ticker-price';
import { getLocalStorageAtom } from '@flipster/react/misc/state/use-local-storage';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import deferred from '@flipster/utils/deferred';
import Num from '@flipster/utils/num';
import { OrderMarginType } from '@flipster/utils/types/base';

import { perpetualSymbolSocketBunja, tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { migrateTradeDefaultsCache } from '../migrate-trade-defaults-cache';

export function resolveTriggerReferencePrice(
  triggerPriceType: TriggerPriceType,
  prices: { lastPrice: string; markPrice: string },
): string {
  return triggerPriceType === triggerPriceTypeEnum.mark ? prices.markPrice : prices.lastPrice;
}

/** Trigger direction 계산에 쓸 기준가가 아직 수신되지 않았는지 판별한다. */
export function isValidTriggerReferencePrice(price: string): boolean {
  return Num(price).isGreaterThan(0);
}

// Mirrors backend `resolve_anchor_price` and iOS PI-11235:
// amount-based → amount; Trigger Limit (fixed) → price; BBO / Trigger Market → triggerPrice.
export const calcPendingOrderNotional = (position: PendingPosition) => {
  if (position.orderVolumeType === 'amount') return Num(position.amount || '0');
  const isTriggerLimitFixed = position.orderType === OrderTypeEnum['StopLimit'] && position.price != null;
  const referencePrice = isTriggerLimitFixed ? position.price : position.triggerPrice;
  return Num(position.qty || '0').multipliedBy(referencePrice ?? '0');
};

/**
 * 해당 심볼의 "체크 가능한" 포지션을 필터링한다.
 * - airdrop 제외, 심볼 일치, 비어있지 않거나(open) anchored pending 주문이 걸린 포지션만.
 *
 * NOTE: pending order 생성 시 서버가 margin mode 판정용 zero position을 만들기 때문에,
 * open position이 없어도 anchored pending의 zero position에서 margin type을 읽을 수 있다.
 */
const filterCheckablePositions = <P extends { airdropType?: unknown; symbol: string; position: string; slot: unknown }>(
  positions: P[] | null | undefined,
  orders: Record<string, { slot: unknown }[]> | null | undefined,
  symbol: string | undefined,
): P[] => {
  if (!symbol || !positions) return [];
  return positions.filter((position) => {
    const isNotAirdrop = position.airdropType == null;
    const isSameSymbol = position.symbol === symbol;
    const isEmptyPosition = position.position === '0';
    const isAnchoredPosition = (orders?.[symbol] ?? []).some((order) => order.slot === position.slot);
    return isNotAirdrop && isSameSymbol && (!isEmptyPosition || isAnchoredPosition);
  });
};

export const symbolOccupiedPositionBunja = bunja(() => {
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const symbolOccupiedPositionAtom = atom((get) => {
    const pendingPositions = get(pendingPositionsAtom);
    const openPositions = get(openPositionsAtom);

    const combined: Record<string, { Long: string; Short: string }> = {};

    // Combine pending positions
    if (pendingPositions) {
      Object.entries(pendingPositions).forEach(([symbol, positions]) => {
        positions.forEach((position: PendingPosition) => {
          const { side } = position;
          const amount = calcPendingOrderNotional(position);

          if (!combined[symbol]) {
            combined[symbol] = { Long: '0', Short: '0' };
          }

          combined[symbol][side] = Num(combined[symbol][side]).plus(amount).toString();
        });
      });
    }

    // Combine open positions
    if (openPositions) {
      Object.entries(openPositions).forEach(([symbol, positions]) => {
        positions.forEach((position: OpenPosition) => {
          const { side } = position;
          const p = Num(position.position || '0');
          const amount = side === 'Long' ? p.multipliedBy(position.askPrice) : p.multipliedBy(position.bidPrice);

          if (!combined[symbol]) {
            combined[symbol] = { Long: '0', Short: '0' };
          }

          combined[symbol][side] = Num(combined[symbol][side]).plus(amount).toString();
        });
      });
    }

    return combined;
  });

  return {
    symbolOccupiedPositionAtom,
  };
});
symbolOccupiedPositionBunja.debugLabel = 'symbolOccupiedPositionBunja';

/**
 * one way mode일때 포지션/오더가 존재하면 leverage를 조정 가능하되 하향은 불가 (Phase 1).
 * hasOneWayPositionAtom: 원웨이 모드에서 포지션/오더가 존재하는지 감지 (Adjust 버튼 표시 및 API 호출 판단)
 * positionLeverageAtom: 현재 포지션/팬딩 포지션의 레버리지 (최소값 제약)
 */
export const oneWayPositionLeverageBunja = bunja(() => {
  const { symbol } = bunja.use(symbolBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { v2AccountAtom } = bunja.use(accountBunja);
  const { v2PositionsAtom } = bunja.use(positionsBunja);

  const currentSymbolPositionAtom = atom((get) => {
    const positions = get(v2PositionsAtom);
    const position = positions?.find((p) => p.symbol === symbol && p.slot === 0);
    if (!position || Num(position.position).isZero()) return undefined;
    return position;
  });

  const currentSymbolPendingPositionsAtom = atom((get) => {
    return get(pendingPositionsAtom)?.[symbol]?.filter((p) => p.slot === 0) ?? [];
  });

  const hasOneWayPositionAtom = atom((get) => {
    const account = get(v2AccountAtom);
    if (account?.tradeMode !== 'OneWay') return false;

    if (get(currentSymbolPositionAtom)) return true;
    if (get(currentSymbolPendingPositionsAtom).length > 0) return true;
    return false;
  });

  const positionLeverageAtom = atom((get) => {
    const account = get(v2AccountAtom);
    if (account?.tradeMode !== 'OneWay') return undefined;
    return (
      get(openPositionsAtom)?.[symbol]?.[0]?.initLeverage ?? get(pendingPositionsAtom)?.[symbol]?.[0]?.initLeverage
    );
  });

  // 현재 열린 포지션의 side (없으면 undefined). reduce-only 취급 판정의 opposite 비교용
  // — bracket.ts canSkipValidateAtom과 동일하게 openPositions를 소스로 쓴다.
  const currentSymbolPositionSideAtom = atom((get) => get(openPositionsAtom)?.[symbol]?.[0]?.side);

  return {
    hasOneWayPositionAtom,
    positionLeverageAtom,
    currentSymbolPositionAtom,
    currentSymbolPositionSideAtom,
    currentSymbolPendingPositionsAtom,
  };
});
oneWayPositionLeverageBunja.debugLabel = 'oneWayPositionLeverageBunja';

export const symbolBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol, symbolInfoAtom: nullableSymbolInfoAtom } = bunja.use(tradeSymbolBunja);
  const { getDetailAtom: pswapDetailAtom, getTickerAtom, getMarketAtom } = bunja.use(perpetualSymbolSocketBunja);
  const symbolInfoAtom = atom((get) => {
    const symbolInfo = get(nullableSymbolInfoAtom);
    if (symbolInfo == null) throw new Error('SymbolInfo cannot be null in perpetual/orderform');
    return symbolInfo;
  });
  const tickerOrSymbolAtom = atom((get) => get(getTickerAtom) ?? get(getMarketAtom));
  const referencePriceAtom = atom((get) => getTickerDisplayPriceOrDefault(get(tickerOrSymbolAtom)));
  const markPriceAtom = atom((get) => get(tickerOrSymbolAtom)?.markPrice ?? get(referencePriceAtom));
  const referencePriceReady = deferred();
  const askPriceAtom = atom((get) => get(pswapDetailAtom)?.askPrice ?? get(referencePriceAtom)); // ask/bid price will always be filled
  const bidPriceAtom = atom((get) => get(pswapDetailAtom)?.bidPrice ?? get(referencePriceAtom));
  bunja.effect(() => {
    return checkReferencePriceReady(), store.sub(tickerOrSymbolAtom, checkReferencePriceReady);
    function checkReferencePriceReady() {
      const tickerOrSymbol = store.get(tickerOrSymbolAtom);
      if (tickerOrSymbol) referencePriceReady.resolve();
    }
  });
  return {
    symbol,
    symbolInfoAtom,
    askPriceAtom,
    bidPriceAtom,
    referencePriceAtom,
    markPriceAtom,
    referencePriceReady,
  };
});
symbolBunja.debugLabel = 'symbolBunja';

export const perpAccountBunja = bunja(() => {
  const { v2AccountAtom } = bunja.use(accountBunja);
  const { getMarginAtom } = bunja.use(marginsBunja);
  const { marginTypeAtom } = bunja.use(marginTypeBunja);
  const { symbol } = bunja.use(symbolBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const isOneWayAtom = atom((get) => get(v2AccountAtom)?.tradeMode === 'OneWay');
  const tradeModeAtom = atom((get) => get(v2AccountAtom)?.tradeMode);
  const availableFundsAtom = atom((get) => {
    const margin = get(getMarginAtom);
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    const instrument = instrumentsKeyBySymbol?.[symbol];
    if (!instrument) return '0';

    const marginType = get(marginTypeAtom);
    switch (marginType) {
      case 'Cross':
        return margin[instrument.quoteCurrency]?.crossMarginAvailable || '0';
      case 'Isolated':
        return Num(margin[instrument.quoteCurrency]?.isolatedMarginAvailable || '0').toString();
      default:
        // oxlint-disable-next-line no-unused-expressions
        marginType satisfies never;
        // oxlint-disable-next-line no-console
        console.error(`Unexpected marginType: ${marginType}`);
        return '0';
    }
  });
  return { isOneWayAtom, tradeModeAtom, availableFundsAtom };
});
perpAccountBunja.debugLabel = 'perpAccountBunja';

// marginType v2 캐시를 읽기 전에 레거시 v1 캐시를 v2로 1회성 마이그레이션하는 공용 storage atom.
// order-form(marginTypeBunja)과 order-form 밖(useSymbolMarginType)이 모두 이 atom을 거쳐 읽어,
// 어떤 진입점에서든 persist atom 생성 전에 마이그레이션이 끝나도록 보장한다. (read-site 누락 방지)
// getLocalStorageAtom은 atom 생성 시점에 localStorage를 스냅샷하고 같은 탭의 setLocalStorage는 통지하지 않으므로,
// 같은 selector 안에서 getLocalStorageAtom 호출 직전에 마이그레이션을 실행한다. (userId 늦은 로드 레이스 방지)
export const marginTypeStorageBunja = bunja(() => {
  const { userAtom } = bunja.use(authBunja);
  const { userIdAtom } = bunja.use(userIdBunja);

  const getMarginTypeStorageAtom = atom((get) => {
    const userId = get(userAtom)?.userId;
    if (userId != null) migrateTradeDefaultsCache(userId);
    return getLocalStorageAtom(`flipster.configs.v2.${get(userIdAtom)}.marginType`, {});
  });

  return { getMarginTypeStorageAtom };
});
marginTypeStorageBunja.debugLabel = 'marginTypeStorageBunja';

// Cross를 지원하지 않는 심볼(USD1 등)은 기본값/캐시가 Cross여도 Isolated로 강제한다.
// margin-type 모달이 crossMarginSupported로 Cross 라디오를 disable하는 것과 동일한 기준이라,
// 선택 불가능한 Cross가 표시상 기본값으로 잡히는 불일치를 막는다.
// crossMarginSupported가 undefined(미확인 instrument)면 강제하지 않는다. (false로 확정된 경우에만 강제)
export const getSupportedMarginType = (
  marginType: OrderMarginType,
  crossMarginSupported: boolean | undefined,
): OrderMarginType => (marginType === 'Cross' && crossMarginSupported === false ? 'Isolated' : marginType);

export const marginTypeFamilyBunja = bunja(() => {
  const { userAtom } = bunja.use(authBunja);
  const { getMarginTypeStorageAtom } = bunja.use(marginTypeStorageBunja);
  const { v2PositionsAtom } = bunja.use(positionsBunja);
  const { v2OrdersAtom } = bunja.use(ordersBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);

  const marginTypeFamily = atomFamily((symbol: string | undefined) =>
    atom((get): OrderMarginType => {
      // 오픈 포지션(또는 anchored pending)이 있으면 엔진 마진 타입이 표시상 우선한다. (캐시에는 쓰지 않음)
      const openPositions = filterCheckablePositions(get(v2PositionsAtom), get(v2OrdersAtom), symbol);
      if (openPositions.length > 0) return openPositions[0].marginType;

      const userDefault = get(userAtom)?.defaultMarginType ?? 'Isolated';
      if (!symbol) return userDefault;

      const marginTypeStorageAtom = get(getMarginTypeStorageAtom);
      const resolved = get(marginTypeStorageAtom)[symbol] ?? userDefault;
      return getSupportedMarginType(resolved, get(instrumentsKeyBySymbolAtom)?.[symbol]?.crossMarginSupported);
    }),
  );

  return { marginTypeFamily };
});
marginTypeFamilyBunja.debugLabel = 'marginTypeFamilyBunja';

export const marginTypeBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol } = bunja.use(symbolBunja);
  const { getMarginTypeStorageAtom } = bunja.use(marginTypeStorageBunja);
  const { marginTypeFamily } = bunja.use(marginTypeFamilyBunja);

  const marginTypeAtom = marginTypeFamily(symbol);

  const updateMarginType = (mode: OrderMarginType) => {
    const marginTypeStorageAtom = store.get(getMarginTypeStorageAtom);
    store.set(marginTypeStorageAtom, (prev) =>
      prev[symbol] === mode
        ? prev
        : {
            ...prev,
            [symbol]: mode,
          },
    );
  };

  return {
    marginTypeAtom,
    updateMarginType,
  };
});
marginTypeBunja.debugLabel = 'marginTypeBunja';

export const useMarginType = () => {
  return useBunja(marginTypeBunja);
};

export const useSymbolMarginType = (symbol: string | undefined): OrderMarginType => {
  const { marginTypeFamily } = useBunja(marginTypeFamilyBunja);
  return useAtomValue(marginTypeFamily(symbol));
};
